import type {
  CustomerEventType,
  CustomerNextEvent,
  LightbaseUser,
  UserSubscriptionStatus,
} from '@/api/users.api'

export function customerDisplayName(user: Pick<LightbaseUser, 'first_name' | 'last_name' | 'email'>): string {
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
}

export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return 'Never'
  const diff = Date.now() - new Date(value).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 60) return `${Math.max(0, minutes)}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatDaysUntil(days: number): string {
  if (days < 0) return `${Math.abs(days)}d overdue`
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  return `In ${days}d`
}

export function eventTypeLabel(type: CustomerEventType): string {
  if (type === 'trial_end') return 'Trial ends'
  if (type === 'renewal') return 'Renews'
  return 'Access ends'
}

export function eventTypeClass(type: CustomerEventType): string {
  if (type === 'trial_end') return 'bg-amber-50 text-amber-800'
  if (type === 'renewal') return 'bg-sky-50 text-sky-800'
  return 'bg-red-50 text-red-700'
}

export function formatNextEvent(event: CustomerNextEvent | null): string {
  if (!event) return '—'
  return `${eventTypeLabel(event.type)} ${formatDaysUntil(event.days_until).toLowerCase()}`
}

export function statusLabel(status: UserSubscriptionStatus): string {
  if (status === 'trialing') return 'Trial'
  if (status === 'paid') return 'Paying'
  if (status === 'cancelling') return 'Cancelling'
  if (status === 'cancelled') return 'Cancelled'
  return 'None'
}

export function statusClass(status: UserSubscriptionStatus): string {
  if (status === 'trialing') return 'bg-amber-50 text-amber-700'
  if (status === 'paid') return 'bg-forest-50 text-forest-700'
  if (status === 'cancelling') return 'bg-orange-50 text-orange-700'
  if (status === 'cancelled') return 'bg-gray-100 text-gray-500'
  return 'bg-gray-100 text-gray-400'
}

export function formatHours(hours: number): string {
  if (hours < 0.1) return `${Math.round((hours || 0) * 60)}m`
  return `${hours.toFixed(1)}h`
}

export function dayBarHeight(seconds: number, maxSeconds: number): string {
  if (!maxSeconds || !seconds) return '2px'
  const pct = Math.max(8, Math.round((seconds / maxSeconds) * 100))
  return `${pct}%`
}

export function urgencyClass(daysUntil: number | null | undefined): string {
  if (daysUntil == null) return 'text-gray-500'
  if (daysUntil <= 1) return 'text-red-600 font-medium'
  if (daysUntil <= 3) return 'text-orange-600 font-medium'
  if (daysUntil <= 7) return 'text-amber-700'
  return 'text-gray-600'
}

/** Sort: soonest next event first; missing events last. */
export function compareByNextEvent(a: LightbaseUser, b: LightbaseUser): number {
  const aDays = a.next_event?.days_until
  const bDays = b.next_event?.days_until
  if (aDays == null && bDays == null) return 0
  if (aDays == null) return 1
  if (bDays == null) return -1
  return aDays - bDays
}

export function compareByInactive(a: LightbaseUser, b: LightbaseUser): number {
  const aDays = a.days_inactive ?? Number.POSITIVE_INFINITY
  const bDays = b.days_inactive ?? Number.POSITIVE_INFINITY
  return bDays - aDays
}
